"use client"

import * as React from "react"
import { Check, Zap, Brain, Info } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const pricingCardVariants = cva(
  "relative w-full min-w-56 max-w-[300px] min-h-[600px] flex flex-col transform overflow-hidden rounded-lg border shadow-lg transition duration-300",
  {
    variants: {
      variant: {
        default: "border-border bg-card",
        outline: "border-border bg-card",
        popular: "border-primary bg-card ring-2 ring-primary",
        ghost: "border-transparent bg-transparent",
      },
      size: {
        default: "p-4",
        sm: "p-4 lg:p-6",
        lg: "p-8 lg:p-10",
      },
      hover: {
        default: "hover:scale-[none] md:hover:scale-105",
        none: "hover:scale-100",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      hover: "default"
    }
  }
)

export interface AICreditsInfo {
  dailyTokens: number
  monthlyTokens: number
  models: string[]
}

export interface PricingCardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pricingCardVariants> {
  heading: string
  description: string
  price: string
  yearlyPrice?: string
  buttonText: string
  list: string[]
  discount?: number
  listHeading?: string
  popular?: boolean
  popularBadge?: string
  onButtonClick?: () => void
  aiCredits?: AICreditsInfo
  userDiscount?: number
}

const PricingCard = React.forwardRef<HTMLDivElement, PricingCardProps>(
  ({ 
    className, 
    variant, 
    size,
    hover,
    heading,
    description,
    price,
    yearlyPrice,
    discount,
    list,
    listHeading,
    buttonText,
    popular,
    popularBadge,
    onButtonClick,
    aiCredits,
    userDiscount,
    ...props 
  }, ref) => {
    const withDiscount = React.useMemo(() => {
      const numericPrice = parseFloat(price.replace(',', '.'))
      return (numericPrice - (numericPrice * (discount ?? 0)) / 100).toFixed(2).replace('.', ',')
    }, [price, discount])

    const calculateYearlyDiscount = React.useMemo(() => {
      if (!yearlyPrice) return 0;
      const monthlyPrice = parseFloat(price.replace(',', '.'));
      const yearlyPriceNum = parseFloat(yearlyPrice.replace(',', '.'));
      const yearlyEquivalent = monthlyPrice * 12;
      const discountPercent = ((yearlyEquivalent - yearlyPriceNum) / yearlyEquivalent) * 100;
      return Math.round(discountPercent);
    }, [price, yearlyPrice])

    const cardVariant = popular ? "popular" : variant

    const formatTokens = (tokens: number): string => {
      if (tokens >= 1_000_000) {
        return `${(tokens / 1_000_000).toFixed(1)}M`;
      } else if (tokens >= 1_000) {
        return `${(tokens / 1_000).toFixed(0)}k`;
      }
      return tokens.toString();
    };

    const getModelDisplayName = (modelId: string): string => {
      const modelNames: { [key: string]: string } = {
        'google/gemini-2.5-flash': 'Gemini Flash',
        'google/gemini-2.5-flash-lite': 'Gemini Lite',
        'google/gemini-2.5-pro': 'Gemini Pro',
        'openai/gpt-5': 'GPT-5',
        'openai/gpt-5-mini': 'GPT-5 Mini'
      };
      return modelNames[modelId] || modelId;
    };

    return (
      <div
        ref={ref}
        className={cn(pricingCardVariants({ variant: cardVariant, size, hover, className }))}
        {...props}
      >
        {popular && popularBadge && (
          <div className="absolute top-0 left-0 right-0 bg-gradient-action text-action-foreground text-center py-2 font-semibold text-sm">
            {popularBadge}
          </div>
        )}

        <div className="flex h-full flex-col justify-between space-y-3">
          <div className={`${popular && popularBadge ? 'pt-8' : ''}`}>
            <h3 className="mb-2 text-2xl font-semibold text-foreground lg:mb-4 lg:text-3xl xl:text-4xl">
              {heading}
            </h3>
            <p className="text-muted-foreground lg:text-base xl:text-xl">
              {description}
            </p>

            {/* AI Credits Display */}
            {aiCredits && (
              <div className="mt-4 space-y-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground cursor-help">
                        <Zap className="w-4 h-4 text-primary" />
                        <span className="font-medium">
                          {formatTokens(aiCredits.dailyTokens)} tokens/dia
                        </span>
                        <Info className="w-3 h-3" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold mb-1">Créditos de IA</p>
                      <p className="text-xs">
                        • Diário: {formatTokens(aiCredits.dailyTokens)} tokens<br />
                        • Mensal: {formatTokens(aiCredits.monthlyTokens)} tokens<br />
                        • Renovação diária automática
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                {/* Available Models */}
                {aiCredits.models && aiCredits.models.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Brain className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="grid grid-cols-2 gap-1.5 flex-1">
                      {aiCredits.models.map((model, idx) => (
                        <Badge key={idx} variant="secondary" className="text-[10px] px-2 py-0.5 truncate">
                          {getModelDisplayName(model)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* User Discount Badge */}
            {userDiscount && userDiscount > 0 && (
              <div className="mt-3">
                <Badge variant="default" className="text-xs">
                  🎉 Seu desconto: -{userDiscount}%
                </Badge>
              </div>
            )}
          </div>

          <div className="flex-1 flex flex-col justify-end">
            <div className="mb-3 flex items-baseline space-x-2">
              <span className="text-2xl font-extrabold text-foreground lg:text-3xl xl:text-4xl">
                R$ {discount ? withDiscount : price}
              </span>
              {discount && (
                <span className="text-muted-foreground line-through md:text-lg lg:text-xl xl:text-2xl">
                  R$ {price}
                </span>
              )}
              <span className="text-muted-foreground">/mês</span>
            </div>

            {/* Yearly Price and Discount */}
            {yearlyPrice && (
              <div className="mb-3 text-sm text-muted-foreground">
                Anual: R$ {yearlyPrice}
                {calculateYearlyDiscount > 0 && (
                  <span className="ml-1 text-success font-medium">
                    ({calculateYearlyDiscount}% desconto)
                  </span>
                )}
              </div>
            )}

            {discount && (
              <div className="absolute right-[-50%] top-0 w-full -translate-x-6 translate-y-4 rotate-45 bg-gradient-action text-action-foreground text-center lg:text-lg xl:text-xl">
                {discount}% OFF
              </div>
            )}

            <Button
              onClick={onButtonClick}
              variant={popular ? "cta" : "outline"}
              className="w-full mb-3"
            >
              {buttonText}
            </Button>

            <ul className="space-y-2 text-sm text-foreground">
              {listHeading && <h5 className="font-semibold text-foreground">{listHeading}</h5>}
              {list.map((text, index) => (
                <li key={index} className="flex items-center">
                  <Check className="mr-3 h-5 w-5 text-success flex-shrink-0" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )
  }
)
PricingCard.displayName = "PricingCard"

export { PricingCard, pricingCardVariants }